import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface ResourceFilterParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const ResourceFilterParameters: React.FC<ResourceFilterParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [excludeDivisions, setExcludeDivisions] = useState<string>(() => {
    const divs = parameters?.['excludeDivisions'];
    if (Array.isArray(divs)) {
      return divs.join(', ');
    }
    return '';
  });
  const [excludePatterns, setExcludePatterns] = useState<string>(() => {
    const patterns = parameters?.['excludePatterns'];
    if (Array.isArray(patterns)) {
      return patterns.join(', ');
    }
    return '';
  });
  const [includePatterns, setIncludePatterns] = useState<string>(() => {
    const patterns = parameters?.['includePatterns'];
    if (Array.isArray(patterns)) {
      return patterns.join(', ');
    }
    return '';
  });
  const [excludeResourceTypes, setExcludeResourceTypes] = useState<string>(() => {
    const types = parameters?.['excludeResourceTypes'];
    if (Array.isArray(types)) {
      return types.join(', ');
    }
    return '';
  });

  useEffect(() => {
    if (onParametersChange) {
      const params: Record<string, unknown> = {};

      const excludeDivsArray = excludeDivisions
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d.length > 0);
      if (excludeDivsArray.length > 0) {
        params['excludeDivisions'] = excludeDivsArray;
      }

      const excludePatternsArray = excludePatterns
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      if (excludePatternsArray.length > 0) {
        params['excludePatterns'] = excludePatternsArray;
      }

      const includePatternsArray = includePatterns
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      if (includePatternsArray.length > 0) {
        params['includePatterns'] = includePatternsArray;
      }

      const excludeTypesArray = excludeResourceTypes
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      if (excludeTypesArray.length > 0) {
        params['excludeResourceTypes'] = excludeTypesArray;
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeDivisions, excludePatterns, includePatterns, excludeResourceTypes]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Resource Filter</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Filter resources based on business rules to determine migration inclusion.
        </p>
      </div>

      <div className="space-y-4">
        {/* Exclude Divisions */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Exclude Divisions
          </label>
          <input
            type="text"
            value={excludeDivisions}
            onChange={(e) => setExcludeDivisions(e.target.value)}
            placeholder="division-id-1, division-id-2"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Comma-separated list of division IDs to exclude
          </p>
        </div>

        {/* Exclude Patterns */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Exclude Patterns (Regex)
          </label>
          <input
            type="text"
            value={excludePatterns}
            onChange={(e) => setExcludePatterns(e.target.value)}
            placeholder="^DEV_.*, ^TEST_.*"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Comma-separated list of regex patterns for names to exclude
          </p>
        </div>

        {/* Include Patterns */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Include Patterns (Regex)
          </label>
          <input
            type="text"
            value={includePatterns}
            onChange={(e) => setIncludePatterns(e.target.value)}
            placeholder="^PROD_.*"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            If specified, ONLY resources matching these patterns will be included
          </p>
        </div>

        {/* Exclude Resource Types */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Exclude Resource Types
          </label>
          <input
            type="text"
            value={excludeResourceTypes}
            onChange={(e) => setExcludeResourceTypes(e.target.value)}
            placeholder="genesyscloud_user, genesyscloud_group"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Comma-separated list of resource types to exclude
          </p>
        </div>

        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-r-lg">
          <div className={`flex items-start ${ui.gap.sm}`}>
            <svg
              className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            <p className={`${ui.text.label} text-yellow-800 font-semibold dark:text-yellow-200 leading-relaxed`}>
              At least one filter criteria must be provided.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceFilterParameters;
